import { useShow, useTranslate } from '@refinedev/core';
import { DateField, Show, useTable } from '@refinedev/antd';
import { Descriptions, Divider, Table, Tabs, Tag, Typography } from 'antd';
import type {
  RevenueCatBillingPeriodOut,
  RevenueCatSubscriptionDetailOut,
  RevenuecatEventOut,
} from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { getSortOrder } from '@/utils/sorters';

const { Title } = Typography;
const monospace = { fontFamily: 'monospace' };

export const RevenueCatSubscriptionShow = () => {
  const t = useTranslate();
  const { query } = useShow<RevenueCatSubscriptionDetailOut>({
    resource: 'monetization.revenuecat_subscriptions',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  const {
    tableProps: billingPeriodsTableProps,
    sorters: billingPeriodsSorters,
  } = useTable<RevenueCatBillingPeriodOut>({
    resource: 'monetization.revenuecat_billing_periods',
    queryOptions: { enabled: record?.id != null },
    filters: {
      permanent: [
        {
          field: 'subscription_id',
          operator: 'eq',
          value: record?.id,
        },
      ],
    },
    sorters: {
      initial: [{ field: 'valid_until', order: 'desc' }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const { tableProps: eventsTableProps, sorters: eventsSorters } =
    useTable<RevenuecatEventOut>({
      resource: 'monetization.revenuecat_events',
      queryOptions: { enabled: record?.id != null },
      filters: {
        permanent: [
          {
            field: 'revenuecat_subscription_id',
            operator: 'eq',
            value: record?.id,
          },
        ],
      },
      sorters: {
        initial: [{ field: 'event_timestamp', order: 'desc' }],
      },
      pagination: { pageSize: 20 },
      syncWithLocation: false,
    });

  return (
    <Show isLoading={isLoading}>
      <Title level={4}>{t('base.sections.basic_info')}</Title>

      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={t('base.fields.id')}>
          {record?.id}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.subscription_id'
          )}
        >
          {record?.subscription_id ? (
            <span style={monospace}>{record.subscription_id}</span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.account_id')}
        >
          {record?.account_id != null ? (
            <RevenueCatCustomerLink
              id={record.account_id}
              title={t(
                'monetization.revenuecat_subscriptions.actions.open_in_revenuecat'
              )}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.customer_id')}
        >
          {record?.customer_id != null ? (
            <ShowLink
              resource="monetization.customers"
              id={record.customer_id}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.store')}
        >
          {record?.store ? <Tag>{record.store}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.environment')}
        >
          {record?.environment ? (
            <Tag
              color={
                record.environment === 'PRODUCTION' ? 'success' : 'warning'
              }
            >
              {record.environment}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.auto_renewal_status'
          )}
        >
          {record?.auto_renewal_status ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.will_renew')}
        >
          {record?.will_renew != null ? (
            <Tag color={record.will_renew ? 'success' : 'default'}>
              {record.will_renew ? t('common.yes') : t('common.no')}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Title level={4}>
        {t('monetization.revenuecat_subscriptions.sections.product')}
      </Title>

      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.product_id')}
        >
          {record?.product_id != null ? (
            <ShowLink resource="monetization.products" id={record.product_id} />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.product_display_name'
          )}
        >
          {record?.product_display_name ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.store_product_identifier'
          )}
        >
          {record?.store_product_identifier ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.revenuecat_product_id'
          )}
        >
          {record?.revenuecat_product_id ? (
            <span style={monospace}>{record.revenuecat_product_id}</span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t(
            'monetization.revenuecat_subscriptions.fields.revenuecat_customer_id'
          )}
          span={2}
        >
          {record?.revenuecat_customer_id ? (
            <span style={monospace}>{record.revenuecat_customer_id}</span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>{t('base.sections.system_info')}</Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.created_at')}
        >
          {record?.created_at ? (
            <DateField value={record.created_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.revenuecat_subscriptions.fields.updated_at')}
        >
          {record?.updated_at ? (
            <DateField value={record.updated_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Tabs
        defaultActiveKey="billing_periods"
        items={[
          {
            key: 'billing_periods',
            label: t(
              'monetization.revenuecat_subscriptions.sections.billing_periods'
            ),
            children: (
              <Table<RevenueCatBillingPeriodOut>
                {...billingPeriodsTableProps}
                pagination={{
                  ...billingPeriodsTableProps.pagination,
                  simple: true,
                }}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('messages.no_data') }}
              >
                <Table.Column
                  dataIndex="id"
                  title={t('base.fields.id')}
                  sorter
                  sortOrder={getSortOrder(billingPeriodsSorters, 'id')}
                  render={(value: number) => (
                    <ShowLink
                      resource="monetization.revenuecat_billing_periods"
                      id={value}
                    />
                  )}
                />
                <Table.Column
                  dataIndex="transaction_id"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.transaction_id'
                  )}
                  render={(value: string) => (
                    <span style={monospace}>{value}</span>
                  )}
                />
                <Table.Column
                  dataIndex="tier"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.tier'
                  )}
                  render={(value: string) =>
                    value ? <Tag color="blue">{value}</Tag> : '-'
                  }
                />
                <Table.Column
                  dataIndex="status"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.status'
                  )}
                  sorter
                  sortOrder={getSortOrder(billingPeriodsSorters, 'status')}
                  render={(value: string) =>
                    value ? (
                      <Tag color={value === 'active' ? 'success' : 'default'}>
                        {t(
                          `monetization.revenuecat_billing_periods.status.${value}`,
                          { defaultValue: value }
                        )}
                      </Tag>
                    ) : (
                      '-'
                    )
                  }
                />
                <Table.Column
                  dataIndex="will_renew"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.will_renew'
                  )}
                  render={(value: boolean | null) =>
                    value == null ? (
                      '-'
                    ) : (
                      <Tag color={value ? 'success' : 'default'}>
                        {value ? t('common.yes') : t('common.no')}
                      </Tag>
                    )
                  }
                />
                <Table.Column
                  dataIndex="valid_from"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.valid_from'
                  )}
                  sorter
                  sortOrder={getSortOrder(billingPeriodsSorters, 'valid_from')}
                  render={(value: string | null) =>
                    value ? <DateField value={value} format="LLL" /> : '-'
                  }
                />
                <Table.Column
                  dataIndex="valid_until"
                  title={t(
                    'monetization.revenuecat_billing_periods.fields.valid_until'
                  )}
                  sorter
                  sortOrder={getSortOrder(billingPeriodsSorters, 'valid_until')}
                  render={(value: string | null) =>
                    value ? <DateField value={value} format="LLL" /> : '-'
                  }
                />
              </Table>
            ),
          },
          {
            key: 'events',
            label: t('monetization.revenuecat_subscriptions.sections.events'),
            children: (
              <Table<RevenuecatEventOut>
                {...eventsTableProps}
                pagination={{
                  ...eventsTableProps.pagination,
                  simple: true,
                }}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('messages.no_data') }}
              >
                <Table.Column
                  dataIndex="id"
                  title={t('base.fields.id')}
                  sorter
                  sortOrder={getSortOrder(eventsSorters, 'id')}
                  render={(value: number) => (
                    <ShowLink
                      resource="monetization.revenuecat_events"
                      id={value}
                    />
                  )}
                />
                <Table.Column
                  dataIndex="event_id"
                  title={t('monetization.revenuecat_events.fields.event_id')}
                  render={(value: string) => (
                    <span style={monospace}>{value}</span>
                  )}
                />
                <Table.Column
                  dataIndex="event_type"
                  title={t('monetization.revenuecat_events.fields.event_type')}
                  render={(value: string | null) =>
                    value ? <Tag>{value}</Tag> : '-'
                  }
                />
                <Table.Column
                  dataIndex="environment"
                  title={t('monetization.revenuecat_events.fields.environment')}
                  render={(value: string | null) => {
                    if (!value) return '-';
                    const color =
                      value === 'PRODUCTION' ? 'success' : 'warning';
                    return <Tag color={color}>{value}</Tag>;
                  }}
                />
                <Table.Column
                  dataIndex="store"
                  title={t('monetization.revenuecat_events.fields.store')}
                  render={(value: string | null) =>
                    value ? <Tag>{value}</Tag> : '-'
                  }
                />
                <Table.Column
                  dataIndex="transaction_id"
                  title={t(
                    'monetization.revenuecat_events.fields.transaction_id'
                  )}
                  render={(value: string | null) =>
                    value ? <span style={monospace}>{value}</span> : '-'
                  }
                />
                <Table.Column
                  dataIndex="event_timestamp"
                  title={t(
                    'monetization.revenuecat_events.fields.event_timestamp'
                  )}
                  sorter
                  sortOrder={getSortOrder(eventsSorters, 'event_timestamp')}
                  render={(value: string | null) =>
                    value ? <DateField value={value} format="LLL" /> : '-'
                  }
                />
              </Table>
            ),
          },
        ]}
      />
    </Show>
  );
};
